import {
    initAppInsights,
    setAppInsightsUserContext,
    clearAppInsightsUserContext,
    trackAppInsightsPageView,
    trackAppInsightsEvent,
    trackAppInsightsException,
} from './appInsights';
import { initClarity, setClarityUserContext, trackClarityEvent } from './clarity';
import { pushToDataLayer } from './gtm';
import type { ObservabilityUserContext } from './types';
import type { FeatureEventName } from './events';

export type { ObservabilityUserContext };
export { FeatureEvent, EventProp, ExceptionPhase, ExceptionProp } from './events';
export type { FeatureEventName } from './events';

/** Initializes all configured telemetry tools. Safe to call unconditionally — each tool no-ops if its env var is unset. */
export function initTelemetry(): void {
    initAppInsights();
    initClarity();
}

/** Tags all tools with the current tenant/user once identity is known (called from BootstrapGate on successful access check). */
export function setUserContext(ctx: ObservabilityUserContext): void {
    setAppInsightsUserContext(ctx);
    setClarityUserContext(ctx);
    pushToDataLayer('user_context', { tenantId: ctx.tenantId });
}

/** Clears tagged identity on logout. */
export function clearUserContext(): void {
    clearAppInsightsUserContext();
}

/** Single source of truth for page-view tracking across all three tools — call on every route change. */
export function trackPageView(path: string): void {
    trackAppInsightsPageView(path, path);
    trackClarityEvent('PageView');
    pushToDataLayer('page_view', { path });
}

/** Records a feature-usage event across App Insights + GTM (+ Clarity as a lightweight event marker). */
export function trackFeatureUsage(name: FeatureEventName, properties?: Record<string, string>): void {
    trackAppInsightsEvent(name, properties);
    trackClarityEvent(name);
    pushToDataLayer(name, properties);
}

/** Records an exception in App Insights. */
export function trackException(error: unknown, properties?: Record<string, string>): void {
    trackAppInsightsException(error, properties);
}
